import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Search,
  Visibility,
  Edit,
  Delete,
  PlayArrow,
  Cancel,
  Add,
  FilterList,
  EmojiEvents,
  Group,
  Schedule,
  AttachMoney,
} from '@mui/icons-material';
import { RootState } from '../store';
import {
  fetchTournaments,
  deleteTournament,
  forceDeleteTournament,
  forceStartTournament,
  cancelTournament,
  clearFilters,
} from '../store/slices/tournamentsSlice';
import { Tournament, TournamentStatus, TournamentFilters } from '../types';
import dayjs from 'dayjs';
import TournamentFormModal from '../components/tournaments/TournamentFormModal';

const TournamentsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { tournaments, pagination, statistics, loading } = useSelector((state: RootState) => state.tournaments);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TournamentStatus | ''>('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showTournamentForm, setShowTournamentForm] = useState(false);
  const [editingTournament, setEditingTournament] = useState<Tournament | null>(null);

  useEffect(() => {
    dispatch(fetchTournaments({ page: 1, limit: 20 }) as any);
  }, [dispatch]);

  const handleSearch = () => {
    const filters: TournamentFilters = {};
    if (searchTerm) filters.search = searchTerm;
    if (statusFilter) filters.status = statusFilter;
    
    dispatch(fetchTournaments({ page: 1, limit: 20, filters }) as any);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(fetchTournaments({ page: newPage + 1, limit: 20 }) as any);
  };

  const handleViewTournament = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    // TODO: Open tournament details modal
  };

  const handleEditTournament = (tournament: Tournament) => {
    setEditingTournament(tournament);
    setShowTournamentForm(true);
  };

  const handleCreateTournament = () => {
    setEditingTournament(null);
    setShowTournamentForm(true);
  };

  const handleDeleteTournament = async (tournament: Tournament) => {
    setSelectedTournament(tournament);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTournament) return;
    
    setDeleteLoading(true);
    try {
      const result = await dispatch(deleteTournament(selectedTournament.id) as any);
      if (result.payload?.success) {
        toast.success('Tournament deleted successfully');
        setShowDeleteDialog(false);
        dispatch(fetchTournaments({ page: pagination.page, limit: 20 }) as any);
      }
    } catch (error) {
      toast.error('Failed to delete tournament');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleForceStart = async (tournamentId: string) => {
    try {
      const result = await dispatch(forceStartTournament(tournamentId) as any);
      if (result.payload) {
        toast.success('Tournament started successfully');
        dispatch(fetchTournaments({ page: pagination.page, limit: 20 }) as any);
      }
    } catch (error) {
      toast.error('Failed to start tournament');
    }
  };

  const handleCancelTournament = async (tournamentId: string) => {
    try {
      const result = await dispatch(cancelTournament(tournamentId) as any);
      if (result.payload) {
        toast.success('Tournament cancelled successfully');
        dispatch(fetchTournaments({ page: pagination.page, limit: 20 }) as any);
      }
    } catch (error) {
      toast.error('Failed to cancel tournament');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  const getStatusColor = (status: TournamentStatus) => {
    switch (status) {
      case 'waiting': return 'default';
      case 'countdown': return 'warning';
      case 'playing': return 'info';
      case 'finished': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: TournamentStatus) => {
    switch (status) {
      case 'waiting': return 'Waiting';
      case 'countdown': return 'Countdown';
      case 'playing': return 'Playing';
      case 'finished': return 'Finished';
      case 'cancelled': return 'Cancelled';
      default: return 'Unknown';
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.5rem", sm: "2rem" }, mb: { xs: 1, sm: 2 } }}>
        Tournaments Management
      </Typography>

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2, md: 3 }, mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <EmojiEvents sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.totalTournaments}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Tournaments
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PlayArrow sx={{ mr: 2, color: 'success.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.activeTournaments}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Active Tournaments
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AttachMoney sx={{ mr: 2, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h6">
                    {formatCurrency(statistics.totalPrizePool)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Prize Pool
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Add sx={{ mr: 2, color: 'info.main' }} />
                <Box>
                  <Typography variant="h6">{tournaments.length}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Current Page
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flex: 1 }}>
            <TextField
              fullWidth
              placeholder="Search tournaments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <FormControl fullWidth sx={{ minWidth: { xs: '100%', sm: 200 } }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as TournamentStatus | '')}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="waiting">Waiting</MenuItem>
                <MenuItem value="countdown">Countdown</MenuItem>
                <MenuItem value="playing">Playing</MenuItem>
                <MenuItem value="finished">Finished</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 1, 
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            {/* First row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  dispatch(clearFilters());
                  dispatch(fetchTournaments({ page: 1, limit: 20 }) as any);
                }}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Clear
              </Button>
            </Box>
            
            {/* Second row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="outlined"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterList />}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Filters
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={handleCreateTournament}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Create 
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Tournaments Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table sx={{ minWidth: { xs: 520, md: 800 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Name</TableCell>
                <TableCell>Stake</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Players</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Start Time</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Prize Pool</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tournaments.map((tournament) => (
                <TableRow key={tournament.id}>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {tournament.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Host: {typeof tournament.hostUserId === 'object' && tournament.hostUserId !== null
                          ? (tournament.hostUserId as any).username || (tournament.hostUserId as any)._id || 'Unknown'
                          : tournament.hostUserId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body1" fontWeight="bold">
                        {formatCurrency(tournament.stake)}
                      </Typography>
                      <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap' }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Group fontSize="small" />
                          <Typography variant="caption">
                            {tournament.currentPlayers}/{tournament.maxPlayers}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                          {formatCurrency(tournament.totalPrize)}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Group fontSize="small" />
                      <Typography variant="body2">
                        {tournament.currentPlayers}/{tournament.maxPlayers}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={getStatusLabel(tournament.status)}
                        color={getStatusColor(tournament.status) as any}
                        size="small"
                      />
                      <Typography variant="caption" color="textSecondary" sx={{ display: { xs: 'block', lg: 'none' } }}>
                        {dayjs(tournament.scheduledStartTime).format('MMM DD, HH:mm')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Schedule fontSize="small" />
                      <Typography variant="body2">
                        {dayjs(tournament.scheduledStartTime).format('MMM DD, HH:mm')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(tournament.totalPrize)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewTournament(tournament)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Tournament">
                        <IconButton
                          size="small"
                          onClick={() => handleEditTournament(tournament)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {tournament.status === 'waiting' && (
                        <Tooltip title="Force Start">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleForceStart(tournament.id)}
                          >
                            <PlayArrow />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(tournament.status === 'waiting' || tournament.status === 'countdown') && (
                        <Tooltip title="Cancel Tournament">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleCancelTournament(tournament.id)}
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete Tournament">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteTournament(tournament)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 20, 50]}
          onRowsPerPageChange={() => {}}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: 'row',
              gap: { xs: 0.5, sm: 1 },
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            },
            '& .MuiTablePagination-spacer': {
              display: 'none',
            },
            '& .MuiTablePagination-selectLabel': {
              display: 'none',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 2, sm: 0 },
            },
            '& .MuiTablePagination-select': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 1, sm: 0 },
            },
            '& .MuiTablePagination-actions': {
              order: { xs: 3, sm: 0 },
            },
          }}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Tournament</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete the tournament "{selectedTournament?.name}"?
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : undefined}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tournament Form Modal */}
      <TournamentFormModal
        tournament={editingTournament}
        open={showTournamentForm}
        onClose={() => setShowTournamentForm(false)}
        onSuccess={() => {
          dispatch(fetchTournaments({ page: pagination.page, limit: 20 }) as any);
        }}
      />
    </Box>
  );
};

export default TournamentsPage; 